import React, { useMemo, useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import * as budgetService from '../../services/budgetService';
import { Card } from '../ui/Card';
import { CategoryIcon } from '../ui/CategoryIcon';
import {
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Edit3,
  IndianRupee,
  LayoutGrid,
  Loader2,
  MessageSquare,
  Plus,
  Save,
  Tag,
  Trash2,
  X
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

// ✅ FIXED: Moving this OUTSIDE the function tells React it never changes.
// This removes the ESLint warning.
const initialState = {
  date: new Date().toISOString().split('T')[0],
  description: '',
  debit: '',
  credit: '',
  category_id: '',
  subcategory_id: ''
};

const SUBCATEGORY_TABLES = ['sub_categories', 'subcategories'];
const fieldClass = "block h-11 w-full min-w-0 max-w-full rounded-xl border border-[#F0F0F0] bg-white px-2.5 py-2 text-[13px] font-bold leading-5 text-black outline-none transition-all focus:border-black disabled:opacity-30 md:px-4 md:py-3 md:text-sm";
const labelClass = "flex h-4 min-w-0 items-center gap-1.5 whitespace-nowrap text-[10px] font-black leading-4 tracking-[0.12em] text-[#888888] md:gap-2 md:text-[11px] md:tracking-[0.18em]";

export default function ExpenseForm({ editTransaction, clearEdit }) {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [subCategoryTable, setSubCategoryTable] = useState(SUBCATEGORY_TABLES[0]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedManagerCategoryId, setSelectedManagerCategoryId] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [newCategoryOpen, setNewCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubcategoryOpen, setNewSubcategoryOpen] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [savingKey, setSavingKey] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const [managerError, setManagerError] = useState('');
  
  const [form, setForm] = useState(initialState);

  // Fetch metadata for dropdowns
  const fetchMeta = async () => {
    const { data: cats } = await supabase.from('categories').select('*').order('name', { ascending: true });
    let subs = [];
    let resolvedTable = subCategoryTable;

    for (const table of SUBCATEGORY_TABLES) {
      const { data, error } = await supabase.from(table).select('*').order('name', { ascending: true });
      if (!error) {
        subs = data || [];
        resolvedTable = table;
        break;
      }
    }

    setCategories((cats || []).filter(c => !c.is_archived && c.category_kind !== 'group' && c.category_kind !== 'subcategory'));
    setSubcategories(subs);
    setSubCategoryTable(resolvedTable);
    setSelectedManagerCategoryId(prev => prev || cats?.[0]?.id || '');
  };

  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Filling and Clearing the form
  useEffect(() => {
    if (editTransaction) {
      setForm({
        date: editTransaction.date || '',
        description: editTransaction.description || '',
        debit: editTransaction.debit || '',
        credit: editTransaction.credit || '',
        category_id: editTransaction.category_id || '',
        subcategory_id: editTransaction.subcategory_id || ''
      });
    } else {
      const requestedType = searchParams.get('type');
      setForm({
        ...initialState,
        debit: requestedType === 'expense' ? '' : '',
        credit: requestedType === 'income' ? '' : ''
      });
    }
  }, [editTransaction, searchParams]);

  const handleSubChange = (subId) => {
    const sub = subcategories.find(s => s.id === subId);
    setForm(prev => ({ 
      ...prev, 
      subcategory_id: subId,
      category_id: sub ? sub.category_id : '' 
    }));
  };

  const flashSaved = (key) => {
    setSavedKey(key);
    setTimeout(() => setSavedKey(''), 1200);
  };

  const startCategoryEdit = (category) => {
    setEditingCategoryId(category.id);
    setEditingSubcategoryId(null);
    setDraftName(category.name || '');
  };

  const startSubcategoryEdit = (subcategory) => {
    setEditingSubcategoryId(subcategory.id);
    setEditingCategoryId(null);
    setDraftName(subcategory.name || '');
  };

  const cancelInlineEdit = () => {
    setEditingCategoryId(null);
    setEditingSubcategoryId(null);
    setDraftName('');
  };

  const saveCategoryName = async (categoryId) => {
    const cleanName = draftName.trim();
    if (!cleanName) return;

    const key = `category-${categoryId}`;
    setSavingKey(key);
    setManagerError('');
    try {
      await budgetService.handleEditCategory(categoryId, { name: cleanName });
      setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, name: cleanName } : c));
      cancelInlineEdit();
      flashSaved(key);
    } catch (err) {
      setManagerError(err.message || 'Could not rename category.');
    } finally {
      setSavingKey('');
    }
  };

  const saveSubcategoryName = async (subcategoryId) => {
    const cleanName = draftName.trim();
    if (!cleanName) return;

    const key = `subcategory-${subcategoryId}`;
    setSavingKey(key);
    setManagerError('');
    const { error } = await supabase.from(subCategoryTable).update({ name: cleanName }).eq('id', subcategoryId);
    setSavingKey('');

    if (!error) {
      setSubcategories(prev => prev.map(s => s.id === subcategoryId ? { ...s, name: cleanName } : s));
      cancelInlineEdit();
      flashSaved(key);
    } else {
      setManagerError(error.message || 'Could not rename sub-category.');
    }
  };

  const createCategory = async () => {
    const cleanName = newCategoryName.trim();
    if (!cleanName) return;

    const key = 'category-new';
    setSavingKey(key);
    setManagerError('');
    try {
      const data = await budgetService.handleAddCategory({
        name: cleanName,
        categoryKind: 'category',
        type: 'Expense',
        iconSlug: 'tag',
        sortOrder: categories.length
      });
      setCategories(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedManagerCategoryId(data.id);
      setNewCategoryName('');
      setNewCategoryOpen(false);
      flashSaved(key);
    } catch (err) {
      setManagerError(err.message || 'Could not create category.');
    } finally {
      setSavingKey('');
    }
  };

  const createSubcategory = async () => {
    const cleanName = newSubcategoryName.trim();
    if (!cleanName || !selectedManagerCategoryId) return;

    const key = 'subcategory-new';
    setSavingKey(key);
    setManagerError('');

    const { data, error } = await supabase
      .from(subCategoryTable)
      .insert([{ name: cleanName, category_id: selectedManagerCategoryId }])
      .select()
      .single();

    setSavingKey('');

    if (!error && data) {
      setSubcategories(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewSubcategoryName('');
      setNewSubcategoryOpen(false);
      flashSaved(key);
    } else {
      setManagerError(error?.message || 'Could not create sub-category.');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!categoryId) return;
    if (subcategories.some(s => s.category_id === categoryId)) {
      setManagerError('Delete or move sub-categories before deleting this category.');
      return;
    }

    const key = `category-delete-${categoryId}`;
    setSavingKey(key);
    setManagerError('');

    try {
      await budgetService.handleDeleteCategory(categoryId);
      setCategories(prev => {
        const next = prev.filter(c => c.id !== categoryId);
        setSelectedManagerCategoryId(next[0]?.id || '');
        return next;
      });
      flashSaved(key);
    } catch (err) {
      setManagerError(err.message || 'Could not delete category.');
    } finally {
      setSavingKey('');
    }
  };

  const deleteSubcategory = async (subcategoryId) => {
    if (!subcategoryId) return;

    const key = `subcategory-delete-${subcategoryId}`;
    setSavingKey(key);
    setManagerError('');

    const { count, error: countError } = await supabase
      .from('transactions')
      .select('id', { count: 'exact', head: true })
      .eq('subcategory_id', subcategoryId);

    if (countError) {
      setSavingKey('');
      setManagerError(countError.message || 'Could not validate sub-category usage.');
      return;
    }

    if (count > 0) {
      setSavingKey('');
      setManagerError('Category contains allocated funds or transactions. Reassign funds to another category before deleting.');
      return;
    }

    const { error } = await supabase.from(subCategoryTable).delete().eq('id', subcategoryId);
    setSavingKey('');

    if (!error) {
      setSubcategories(prev => prev.filter(s => s.id !== subcategoryId));
      flashSaved(key);
    } else {
      setManagerError(error.message || 'Could not delete sub-category.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();

    const payload = {
      ...form,
      user_id: user.id,
      debit: form.debit || null,
      credit: form.credit || null
    };

    let error;
    let transaction = null;
    if (editTransaction) {
      const result = await supabase.from('transactions').update(payload).eq('id', editTransaction.id);
      error = result.error;
    } else {
      const result = await supabase.from('transactions').insert([payload]).select().single();
      error = result.error;
      transaction = result.data;
    }

    setLoading(false);
    if (!error) {
      // ─ AUTO-UPDATE BUDGET ENVELOPE + AGE OF MONEY (Non-Breaking) ───────
      if (transaction?.debit > 0 && user?.id) {
        try {
          // Update budget envelope if category is set
          if (transaction.category_id) {
            const monthStr = transaction.date.slice(0, 7) + '-01';
            const budget = await budgetService.getBudgetForMonth(user.id, new Date(monthStr));
            if (budget?.id) {
              await budgetService.recordTransactionToEnvelope(budget.id, transaction.category_id, transaction.debit);
            }
          }
          // FIFO money-flow: sets money_age_days on the saved transaction
          await budgetService.recordMoneyFlow(user.id, transaction);
        } catch (err) {
          // Non-critical: never fail the transaction save
        }
      }
      // ─────────────────────────────────────────────────────────────────────
      
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        if (clearEdit) clearEdit();
      }, 1500);
      
      if (!editTransaction) setForm(initialState);
    }
  };

  const selectedCategory = categories.find(c => c.id === form.category_id);
  const selectedCategoryName = selectedCategory?.name || 'Select sub-category';
  const selectedCategoryType = selectedCategory?.type;
  const selectedManagerIndex = Math.max(categories.findIndex(c => c.id === selectedManagerCategoryId), 0);
  const managerSubcategories = useMemo(
    () => subcategories.filter(s => s.category_id === selectedManagerCategoryId),
    [subcategories, selectedManagerCategoryId]
  );
  const selectedManagerCategory = categories.find(c => c.id === selectedManagerCategoryId);
  const stepCategory = (direction) => {
    if (categories.length === 0) return;
    const currentIndex = categories.findIndex(c => c.id === selectedManagerCategoryId);
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (safeIndex + direction + categories.length) % categories.length;
    setSelectedManagerCategoryId(categories[nextIndex].id);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-4xl space-y-4 overflow-hidden pb-24 font-sans md:space-y-6 md:pb-28">
      <Card className="w-full max-w-full overflow-hidden border border-[#F0F0F0] bg-white p-3 sm:p-4 md:p-8">
        
        {/* Header */}
        <div className="mb-3 flex flex-col gap-2 border-b border-[#F0F0F0] pb-3 sm:flex-row sm:items-center sm:justify-between md:mb-8 md:gap-4 md:pb-6">
          <div className="min-w-0">
            <h2 className="text-2xl font-black leading-none tracking-tight text-black md:text-3xl">
              {editTransaction ? 'Update Entry' : searchParams.get('type') === 'income' ? 'Add Income' : 'Add Expense'}
            </h2>
            <p className="mt-1.5 text-[9px] font-black uppercase tracking-[0.2em] text-[#888888] md:mt-2 md:text-xs">Expense manager</p>
          </div>
          {editTransaction && (
            <button 
              type="button"
              onClick={clearEdit} 
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#F0F0F0] bg-white px-4 py-2 text-xs font-bold uppercase text-rose-700 transition-all hover:bg-rose-50 sm:w-auto"
            >
              <X size={14} /> Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 md:space-y-6">
          
          <div className="grid min-w-0 grid-cols-2 gap-2.5 md:gap-6">
            {/* Date */}
            <div className="min-w-0 space-y-1 md:space-y-2">
              <label className={labelClass}>
                <Calendar size={13} strokeWidth={1.7} className="shrink-0" /> Date
              </label>
              <input 
                type="date" 
                className={`${fieldClass} px-1.5 text-[12px] sm:px-2`}
                value={form.date}
                onChange={e => setForm({...form, date: e.target.value})}
                required
              />
            </div>

            {/* Subcategory */}
            <div className="min-w-0 space-y-1 md:space-y-2">
              <label className={labelClass}>
                <Tag size={13} strokeWidth={1.7} className="shrink-0" /> Sub-category
              </label>
              <select 
                className={`${fieldClass} cursor-pointer appearance-none`}
                value={form.subcategory_id}
                onChange={e => handleSubChange(e.target.value)}
                required
              >
                <option value="">Select sub-category</option>
                {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Main Category Display */}
          <div className="grid min-w-0 grid-cols-2 gap-2.5 md:gap-6">
          <div className="min-w-0 space-y-1 md:space-y-2">
            <label className={labelClass}>
              <LayoutGrid size={13} strokeWidth={1.7} className="shrink-0" /> Main cat.
            </label>
            <div className={`flex h-11 w-full max-w-full items-center gap-2 overflow-hidden rounded-xl border px-2.5 text-xs font-bold transition-colors md:gap-3 md:px-4 md:text-sm ${form.category_id ? 'bg-[#111111] border-[#111111] text-white' : 'bg-white border-[#F0F0F0] text-[#888888]'}`}>
              {form.category_id && (
                <CategoryIcon iconSlug={selectedCategory?.icon_slug} className="h-7 w-7 shrink-0 border-white/10 bg-white/10 text-slate-300 md:h-9 md:w-9" size={16} />
              )}
              <span className="truncate">{selectedCategoryName}</span>
            </div>
          </div>

          {/* Description */}
          <div className="min-w-0 space-y-1 md:space-y-2">
            <label className={labelClass}>
              <MessageSquare size={13} strokeWidth={1.7} className="shrink-0" /> Note
            </label>
            <input 
              type="text" 
              placeholder="Optional note"
              className={fieldClass}
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
            />
          </div>
          </div>

          {/* Amount Area */}
          <div className="rounded-2xl border border-[#F0F0F0] bg-white p-3 md:p-6">
             <div className="grid min-w-0 grid-cols-2 gap-3 md:gap-6">
                <div className="min-w-0 space-y-1 md:space-y-2">
                  <label className={labelClass}>
                    <IndianRupee size={13} strokeWidth={1.7} className="shrink-0 text-rose-500" /> Spent
                  </label>
                  <input 
                    type="number" 
                    step="0.01"
                    disabled={selectedCategoryType === 'Income'}
                    inputMode="decimal"
                    placeholder="0.00"
                    className="h-11 w-full min-w-0 rounded-xl border-0 border-b-2 border-rose-500 bg-white px-1 py-2 text-base font-black leading-5 text-black outline-none transition-all disabled:opacity-20 md:h-14 md:text-xl"
                    value={form.debit}
                    onChange={e => setForm({...form, debit: e.target.value, credit: ''})}
                  />
                </div>
                <div className="min-w-0 space-y-1 md:space-y-2">
                  <label className={labelClass}>
                    <IndianRupee size={13} strokeWidth={1.7} className="shrink-0 text-emerald-500" /> Received
                  </label>
                  <input 
                    type="number" 
                    step="0.01"
                    disabled={selectedCategoryType === 'Expense'}
                    inputMode="decimal"
                    placeholder="0.00"
                    className="h-11 w-full min-w-0 rounded-xl border-0 border-b-2 border-emerald-500 bg-white px-1 py-2 text-base font-black leading-5 text-black outline-none transition-all disabled:opacity-20 md:h-14 md:text-xl"
                    value={form.credit}
                    onChange={e => setForm({...form, credit: e.target.value, debit: ''})}
                  />
                </div>
             </div>
          </div>

          {/* Submit */}
          <button 
            type="submit" 
            disabled={loading}
            className={`flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-black text-white shadow-[0_10px_24px_rgba(0,0,0,0.16)] transition-all active:scale-[0.98] md:h-auto md:gap-3 md:py-4 md:text-base md:shadow-[0_18px_40px_rgba(0,0,0,0.22)] ${
              success ? 'bg-emerald-500' : 'bg-black hover:bg-[#111111]'
            }`}
          >
            {loading ? 'Processing...' : success ? 'Successfully Saved!' : (
              <>
                <Save size={20} />
                {editTransaction ? 'Save Changes' : 'Add Transaction'}
              </>
            )}
          </button>
        </form>
      </Card>

      <Card className="w-full max-w-full overflow-hidden border border-[#F0F0F0] bg-white p-4 md:p-8">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-black tracking-tight text-black">Manage Categories</h2>
            <p className="mt-2 text-xs font-black uppercase tracking-[0.2em] text-[#888888]">List and chip manager</p>
          </div>
          <button
            type="button"
            onClick={() => setNewCategoryOpen(true)}
            className="grid h-11 w-11 place-items-center rounded-xl border border-[#F0F0F0] bg-white text-black transition hover:border-black"
            aria-label="New category"
          >
            <Plus size={20} strokeWidth={1.7} />
          </button>
        </div>

        {newCategoryOpen && (
          <div className="mb-5 flex w-full max-w-full gap-2 rounded-2xl border border-[#F0F0F0] p-2">
            <input
              className="min-w-0 flex-1 rounded-xl border-0 px-3 py-2 text-sm font-bold text-black outline-none"
              placeholder="New category name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              autoFocus
            />
            <button type="button" onClick={createCategory} className="grid h-10 w-10 place-items-center rounded-xl bg-black text-white">
              {savingKey === 'category-new' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            </button>
            <button type="button" onClick={() => setNewCategoryOpen(false)} className="grid h-10 w-10 place-items-center rounded-xl text-[#888888]">
              <X size={16} />
            </button>
          </div>
        )}

        {managerError && (
          <div className="mb-5 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
            {managerError}
          </div>
        )}

        <div className="grid w-full max-w-full grid-cols-[44px_minmax(0,1fr)_44px] items-center gap-3 rounded-2xl border border-[#F0F0F0] bg-white p-2">
          <button
            type="button"
            onClick={() => stepCategory(-1)}
            className="grid h-11 w-11 place-items-center rounded-xl border border-[#F0F0F0] text-black transition hover:border-[#0077FF] hover:text-[#0077FF] disabled:opacity-30"
            aria-label="Previous category"
            disabled={categories.length <= 1}
          >
            <ChevronLeft size={20} strokeWidth={1.7} />
          </button>

          <div className="min-w-0 px-1 text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#888888]">
              Category {categories.length ? selectedManagerIndex + 1 : 0} / {categories.length}
            </p>
            {selectedManagerCategory && editingCategoryId === selectedManagerCategory.id ? (
              <div className="mx-auto mt-2 flex max-w-full items-center gap-2 rounded-xl bg-[#F8F8F8] px-3 py-2">
                <input
                  className="min-w-0 flex-1 bg-transparent text-center text-sm font-black text-black outline-none"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveCategoryName(selectedManagerCategory.id)}
                  autoFocus
                />
                <button type="button" onClick={() => saveCategoryName(selectedManagerCategory.id)} className="shrink-0 text-black">
                  {savingKey === `category-${selectedManagerCategory.id}` ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                </button>
                <button type="button" onClick={cancelInlineEdit} className="shrink-0 text-[#888888]">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="mt-1 flex min-w-0 items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => selectedManagerCategory && startCategoryEdit(selectedManagerCategory)}
                  className="min-w-0 truncate text-lg font-black text-black"
                >
                  {selectedManagerCategory?.name || 'No category'}
                </button>
                {selectedManagerCategory && savedKey === `category-${selectedManagerCategory.id}` ? (
                  <CheckCircle2 size={15} className="shrink-0 text-emerald-500" />
                ) : (
                  <Edit3 size={15} strokeWidth={1.7} className="shrink-0 text-[#888888]" />
                )}
                {selectedManagerCategory && (
                  <button
                    type="button"
                    onClick={() => deleteCategory(selectedManagerCategory.id)}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[#888888] transition hover:bg-rose-50 hover:text-rose-600"
                    aria-label="Delete category"
                  >
                    {savingKey === `category-delete-${selectedManagerCategory.id}` ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} strokeWidth={1.7} />}
                  </button>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => stepCategory(1)}
            className="grid h-11 w-11 place-items-center rounded-xl border border-[#F0F0F0] text-black transition hover:border-[#0077FF] hover:text-[#0077FF] disabled:opacity-30"
            aria-label="Next category"
            disabled={categories.length <= 1}
          >
            <ChevronRight size={20} strokeWidth={1.7} />
          </button>
        </div>

        <div className="mt-6 rounded-3xl border border-[#F0F0F0] p-5 md:p-6">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#888888]">Sub-categories</p>
              {selectedManagerCategory && editingCategoryId === selectedManagerCategory.id ? (
                <div className="mt-2 flex max-w-full items-center gap-2 rounded-xl bg-[#F8F8F8] px-3 py-2">
                  <input
                    className="min-w-0 flex-1 bg-transparent text-xl font-black text-black outline-none"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveCategoryName(selectedManagerCategory.id)}
                    autoFocus
                  />
                  <button type="button" onClick={() => saveCategoryName(selectedManagerCategory.id)} className="shrink-0 text-black">
                    {savingKey === `category-${selectedManagerCategory.id}` ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  </button>
                  <button type="button" onClick={cancelInlineEdit} className="shrink-0 text-[#888888]">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="mt-1 flex min-w-0 items-center gap-2">
                  <h3 className="min-w-0 truncate text-xl font-black text-black">{selectedManagerCategory?.name || 'Select a category'}</h3>
                  {selectedManagerCategory && (
                    <>
                      <button
                        type="button"
                        onClick={() => startCategoryEdit(selectedManagerCategory)}
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[#888888] transition hover:bg-[#F8F8F8] hover:text-black"
                        aria-label="Rename category"
                      >
                        <Edit3 size={15} strokeWidth={1.7} />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteCategory(selectedManagerCategory.id)}
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[#888888] transition hover:bg-rose-50 hover:text-rose-600"
                        aria-label="Delete category"
                      >
                        {savingKey === `category-delete-${selectedManagerCategory.id}` ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} strokeWidth={1.7} />}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {savedKey.startsWith('subcategory-') && (
                <div className="flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-600">
                  <CheckCircle2 size={14} /> Saved
                </div>
              )}
              <button
                type="button"
                onClick={() => setNewSubcategoryOpen(true)}
                className="grid h-10 w-10 place-items-center rounded-xl border border-[#F0F0F0] bg-white text-black transition hover:border-black disabled:opacity-30"
                aria-label="New sub-category"
                disabled={!selectedManagerCategoryId}
              >
                <Plus size={18} strokeWidth={1.7} />
              </button>
            </div>
          </div>

          {newSubcategoryOpen && (
            <div className="mb-5 flex w-full max-w-full gap-2 rounded-2xl border border-[#F0F0F0] p-2">
              <input
                className="min-w-0 flex-1 rounded-xl border-0 px-3 py-2 text-sm font-bold text-black outline-none"
                placeholder="New sub-category name"
                value={newSubcategoryName}
                onChange={(e) => setNewSubcategoryName(e.target.value)}
                autoFocus
              />
              <button type="button" onClick={createSubcategory} className="grid h-10 w-10 place-items-center rounded-xl bg-black text-white">
                {savingKey === 'subcategory-new' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              </button>
              <button type="button" onClick={() => setNewSubcategoryOpen(false)} className="grid h-10 w-10 place-items-center rounded-xl text-[#888888]">
                <X size={16} />
              </button>
            </div>
          )}

          <div className="space-y-2">
            {managerSubcategories.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-[#F0F0F0] px-4 py-8 text-center text-sm font-bold text-[#888888]">
                No sub-categories found.
              </p>
            ) : managerSubcategories.map(subcategory => {
              const key = `subcategory-${subcategory.id}`;
              return (
                <div key={subcategory.id} className="flex items-center justify-between gap-3 rounded-2xl border border-[#F0F0F0] px-4 py-3">
                  {editingSubcategoryId === subcategory.id ? (
                    <input
                      className="min-w-0 flex-1 text-sm font-black text-black outline-none"
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && saveSubcategoryName(subcategory.id)}
                      autoFocus
                    />
                  ) : (
                    <button type="button" onClick={() => handleSubChange(subcategory.id)} className="min-w-0 flex-1 text-left text-sm font-black text-black">
                      {subcategory.name}
                    </button>
                  )}

                  <div className="flex items-center gap-2 text-[#888888]">
                    {editingSubcategoryId === subcategory.id ? (
                      <>
                        <button type="button" onClick={() => saveSubcategoryName(subcategory.id)}>
                          {savingKey === key ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                        </button>
                        <button type="button" onClick={cancelInlineEdit}><X size={16} /></button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => startSubcategoryEdit(subcategory)}>
                          {savedKey === key ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Edit3 size={15} strokeWidth={1.7} />}
                        </button>
                        <button type="button" onClick={() => deleteSubcategory(subcategory.id)} className="text-[#888888] transition hover:text-rose-600">
                          {savingKey === `subcategory-delete-${subcategory.id}` ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={15} strokeWidth={1.7} />}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
